export function setup() {
  ((self) => {
    let isPolyfillNeeded = false;
    try {
      new self.EventTarget();
    } catch {
      isPolyfillNeeded = true;
    }
    if (!isPolyfillNeeded) {
      return;
    }
    function normalizeOptions(options) {
      if (typeof options === "boolean") {
        return { capture: options };
      }
      return options || {};
    }
    class EventTarget {
      _listeners;
      constructor() {
        this._listeners = /* @__PURE__ */ new Map();
      }
      addEventListener(type, listener, options) {
        if (arguments.length < 2) {
          throw new TypeError(`Failed to execute 'addEventListener' on 'EventTarget': 2 arguments required, but only ${arguments.length} present.`);
        }
        type = String(type);
        if (listener === null || listener === void 0) {
          return;
        }
        if (typeof listener !== "function" && typeof listener !== "object") {
          return;
        }
        if (typeof listener === "object" && typeof listener.handleEvent !== "function") {
          return;
        }
        const normalizedOptions = normalizeOptions(options);
        const {
          capture = false,
          once = false,
          passive = false,
          signal
        } = normalizedOptions;
        if (signal && signal.aborted) {
          return;
        }
        if (!this._listeners.has(type)) {
          this._listeners.set(type, []);
        }
        const listeners = this._listeners.get(type);
        const existingIndex = listeners.findIndex(
          (wrapper2) => wrapper2.listener === listener && wrapper2.capture === capture
        );
        if (existingIndex !== -1) {
          return;
        }
        const callback = (event) => {
          if (passive) {
            const originalPreventDefault = event.preventDefault;
            event.preventDefault = () => {
            };
            try {
              if (typeof listener === "function") {
                listener.call(this, event);
              } else {
                listener.handleEvent(event);
              }
            } finally {
              event.preventDefault = originalPreventDefault;
            }
          } else {
            if (typeof listener === "function") {
              listener.call(this, event);
            } else {
              listener.handleEvent(event);
            }
          }
          if (once) {
            this.removeEventListener(type, listener, options);
          }
        };
        const wrapper = {
          listener,
          callback,
          options: normalizedOptions,
          capture,
          once,
          passive,
          signal
        };
        listeners.push(wrapper);
        if (signal) {
          const abortHandler = () => {
            this.removeEventListener(type, listener, options);
          };
          signal.addEventListener("abort", abortHandler, { once: true });
        }
      }
      dispatchEvent(event) {
        if (arguments.length < 1) {
          throw new TypeError(`Failed to execute 'dispatchEvent' on 'EventTarget': 1 argument required, but only ${arguments.length} present.`);
        }
        if (!event || typeof event !== "object") {
          throw new TypeError(`Failed to execute 'dispatchEvent' on 'EventTarget': parameter 1 is not of type 'Event'.`);
        }
        const type = event.type;
        if (!this._listeners.has(type)) {
          return true;
        }
        const listeners = this._listeners.get(type).slice();
        Object.defineProperty(event, "target", {
          value: this,
          writable: false,
          configurable: true
        });
        Object.defineProperty(event, "currentTarget", {
          value: this,
          writable: false,
          configurable: true
        });
        for (const wrapper of listeners) {
          try {
            wrapper.callback.call(this, event);
          } catch (error) {
            setTimeout(() => {
              throw error;
            }, 0);
          }
          if (event._stopImmediatePropagation) {
            break;
          }
        }
        return !event.defaultPrevented;
      }
      removeEventListener(type, listener, options) {
        if (arguments.length < 2) {
          throw new TypeError(`Failed to execute 'removeEventListener' on 'EventTarget': 2 arguments required, but only ${arguments.length} present.`);
        }
        type = String(type);
        if (listener === null || listener === void 0) {
          return;
        }
        const { capture = false } = normalizeOptions(options);
        if (!this._listeners.has(type)) {
          return;
        }
        const listeners = this._listeners.get(type);
        const index = listeners.findIndex(
          (wrapper) => wrapper.listener === listener && wrapper.capture === capture
        );
        if (index !== -1) {
          listeners.splice(index, 1);
        }
        if (listeners.length === 0) {
          this._listeners.delete(type);
        }
      }
    }
    if (self.EventTarget) {
      Object.setPrototypeOf(EventTarget.prototype, self.EventTarget.prototype);
    }
    self.EventTarget = EventTarget;
  })(window);
  (() => {
  })(window);
  ((self) => {
    const isPolyfillNeeded = !("AbortController" in self && typeof self.AbortController === "function");
    if (!isPolyfillNeeded) {
      return;
    }
    const SECRET = Symbol("abort signal secret");
    class AbortSignal extends EventTarget {
      _aborted = false;
      _onabort = null;
      constructor(secret) {
        if (secret !== SECRET) {
          throw new TypeError("Illegal constructor");
        }
        super();
      }
      get aborted() {
        return this._aborted;
      }
      get onabort() {
        return this._onabort;
      }
      set onabort(handler) {
        if (this._onabort) {
          this.removeEventListener("abort", this._onabort);
        }
        this._onabort = handler;
        if (handler) {
          this.addEventListener("abort", handler);
        }
      }
    }
    class AbortController {
      _signal;
      constructor() {
        this._signal = new AbortSignal(SECRET);
      }
      get signal() {
        return this._signal;
      }
      abort() {
        if (this._signal.aborted) {
          return;
        }
        this._signal._aborted = true;
        const event = new Event("abort");
        this._signal.dispatchEvent(event);
      }
    }
    self.AbortSignal = AbortSignal;
    self.AbortController = AbortController;
  })(window);
}